/**
 * RÉFÉRENTIELS → SOUS-FAMILLES — un second niveau LIBRE, non rattaché à une famille (maquette, onglet `sousfams`) :
 * « Jean brut » sert en Pantalons comme en Vestes.
 *
 * ⚠️ LA MAQUETTE DESSINE LE BOUTON ET LES CROIX SANS RIEN DERRIÈRE. En production un bouton qui ne fait rien est
 *    interdit (§23.0) : « Nouvelle sous-famille » ouvre une fenêtre qui crée vraiment, et la croix supprime vraiment —
 *    offerte seulement quand rien ne porte la sous-famille, sinon la pastille dit pourquoi elle reste.
 * ⚠️ PAS D'ORDRE : rien ne range les sous-familles (ni écran, ni caisse) — elles se lisent par libellé.
 */
import { useState } from 'react';
import { checkLabel, confirm, Icon, ModalBackdrop, showToast, Trans, useOverlayPanel } from '@orgazone/ui-web';
import { api, type ReferentialFacetEntry, type ReferentialFacetListing } from '../api';
import { t } from '../i18n';
import { FacetError, FacetOrphans, FacetReadFailure, reasonOf } from './referential-facets-shared';

export function SubFamiliesTab({ listing, failure, reload }: {
  listing: ReferentialFacetListing | null;
  failure: string | null;
  reload: () => void;
}) {
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (failure) return <FacetReadFailure failure={failure} />;
  if (!listing) return <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture du référentiel…')}</p>;
  const rows = listing.rows;

  const create = async (v: string) => {
    await api.createMasterDataOnly('sub_family', { code: v, label: v }, `sub_family:create:${v}`);
    showToast(t('Sous-famille créée · {v}', { v }));
  };

  const remove = async (s: ReferentialFacetEntry) => {
    const ok = await confirm({
      title: t('Supprimer la sous-famille « {v} » ?', { v: s.label }), message: t('Aucun article ne l’utilise — suppression sans impact.'),
      confirmLabel: t('Supprimer'), cancelLabel: t('Annuler'), tone: 'danger',
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await api.deleteReferentielEntry('sub_family', { code: s.code }, `sub_family:delete:${s.code}`);
      showToast(t('Sous-famille supprimée · {v}', { v: s.label }), 'info');
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  const addOrphan = async (v: string) => {
    setBusy(true); setError(null);
    try { await create(v); } catch (e) { setError(reasonOf(e)); }
    setBusy(false);
    reload();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-title)', color: 'var(--ink-600)' }}>
          <Trans k="<1>{n}</1> sous-famille" vars={{ n: rows.length }}
            parts={[(x) => <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} />
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => setCreating(true)} className="oz-ref-outline"
          style={{
            height: 38, padding: '0 15px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
            color: 'var(--teal-700)', fontSize: 'var(--t-title)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
          }}>{t('Nouvelle sous-famille')}</button>
      </div>
      {error && <div style={{ marginBottom: 12, borderRadius: 'var(--r-sm)', overflow: 'hidden' }}><FacetError message={error} /></div>}
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: 14 }}>
        {rows.length === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Aucune sous-famille dans la liste.')}</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {rows.map((s) => (
              <span key={s.code}
                title={s.carriers > 0 ? t('Portée par {n} article — non supprimable', { n: s.carriers }) : undefined}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, height: 34, padding: s.carriers === 0 ? '0 8px 0 13px' : '0 13px',
                  borderRadius: 'var(--r-pill)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
                  fontSize: 'var(--t-lead)', fontWeight: 500, color: s.isActive ? 'var(--ink-800)' : 'var(--ink-400)',
                }}>
                {s.label}
                {s.carriers === 0 && (
                  <button type="button" aria-label={t('Retirer « {v} »', { v: s.label })} disabled={busy} onClick={() => void remove(s)} className="oz-ref-chip-x"
                    style={{
                      width: 22, height: 22, border: 'none', borderRadius: 'var(--r-pill)', background: 'var(--ink-200)', color: 'var(--ink-600)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0, cursor: busy ? 'default' : 'pointer',
                    }}><Icon name="x" size={11} stroke={2.6} /></button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
      <div style={{
        marginTop: 16, padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
        fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6,
      }}>
        <Trans k="Les sous-familles sont un <1>second niveau libre</1>, non rattaché à une famille précise : « Jean brut » peut servir en Pantalons comme en Vestes. C’est volontaire — imposer le rattachement obligerait à dupliquer les mêmes libellés dans plusieurs familles."
          parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} />
      </div>
      <FacetOrphans orphans={listing.orphans} busy={busy} onAdd={(v) => void addOrphan(v)} />
      {creating && (
        <SubFamilyDialog existing={rows.map((r) => r.label)} onClose={() => setCreating(false)}
          onCreate={create} onSaved={() => { setCreating(false); reload(); }} />
      )}
    </div>
  );
}

/** La fenêtre d'une sous-famille neuve — son nom, jugé comme celui d'une famille (déjà prise, ou proche). */
function SubFamilyDialog({ existing, onClose, onCreate, onSaved }: {
  existing: readonly string[];
  onClose: () => void;
  onCreate: (v: string) => Promise<void>;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { panelRef } = useOverlayPanel({ open: true, onClose, autoFocus: 'first', blockClose: () => saving });
  const verdict = checkLabel(name, existing);
  const blocked = verdict.etat === 'vide' || verdict.etat === 'exact';

  const save = async () => {
    if (blocked || saving) return;
    setSaving(true); setError(null);
    try { await onCreate(verdict.valeur); onSaved(); } catch (e) { setError(reasonOf(e)); setSaving(false); }
  };

  return (
    <ModalBackdrop onBackdrop={saving ? undefined : onClose}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={t('Nouvelle sous-famille')} tabIndex={-1} style={{
        position: 'relative', width: 472, maxWidth: '94vw', background: 'var(--paper)', borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--sh-modal)', overflow: 'hidden', animation: 'pop .18s cubic-bezier(.2,.7,.3,1)',
      }}>
        <div style={{ height: 3, background: 'linear-gradient(90deg,var(--teal-500),var(--teal-300))' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-h3)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>{t('Nouvelle sous-famille')}</span>
          <button type="button" onClick={onClose} disabled={saving} aria-label={t('Fermer')}
            style={{ width: 34, height: 34, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0, cursor: 'pointer' }}>
            <Icon name="x" size={16} stroke={2} />
          </button>
        </div>
        <div style={{ padding: '20px 22px' }}>
          <label htmlFor="ref-sub-family-name" style={{ display: 'block', fontSize: 'var(--t-note)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5 }}>
            {t('Nom de la sous-famille')}</label>
          <input id="ref-sub-family-name" value={name} placeholder={t('Jean brut')} onChange={(e) => { setName(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void save(); } }} className="oz-ref-input"
            style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-title)', outline: 'none', boxSizing: 'border-box' }} />
          {verdict.etat === 'exact' && (
            <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--amber-fg)', fontWeight: 600, marginTop: 6 }}>
              {t('« {v} » existe déjà.', { v: verdict.candidat })}</span>
          )}
          {verdict.etat === 'proche' && (
            <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--amber-fg)', fontWeight: 600, marginTop: 6 }}>
              {t('Ressemble à « {v} » — {pct} %.', { v: verdict.candidat, pct: verdict.pourcent })}</span>
          )}
        </div>
        {error && <FacetError message={error} />}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={saving}
            style={{ height: 40, padding: '0 18px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-title)', fontWeight: 600, cursor: 'pointer' }}>
            {t('Annuler')}</button>
          <button type="button" onClick={() => void save()} disabled={blocked || saving}
            style={{
              height: 40, padding: '0 18px', border: 'none', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-title)', fontWeight: 700,
              background: blocked ? 'var(--ink-200)' : 'var(--teal-500)', color: blocked ? 'var(--ink-400)' : '#fff', cursor: blocked || saving ? 'not-allowed' : 'pointer',
            }}>{verdict.etat === 'proche' ? t('Enregistrer quand même') : t('Enregistrer')}</button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
